import FormLabel from '@c/forms/controls/FormLabel';
import BaseModal from '@c/modals/BaseModal';
import { useQuery } from '@tanstack/react-query';
import Button from '@ui/Button';
import { Label } from '@ui/Label';
import Select from '@ui/Select';
import {
  getMainCatsClient,
  getSubcatsClient,
} from '@util/firestore/categories';
import { CategoryDocument } from 'models/category';
import { useState } from 'react';
import { useCreateListing } from '../_context';

export default function EditCategoryModal({
  isOpen,
  dismiss,
}: {
  isOpen: boolean;
  dismiss: () => void;
}) {
  const { categoryInfo, setCategoryInfo, form } = useCreateListing();
  const [selected, setSelected] = useState<CategoryDocument[]>(categoryInfo ?? []);
  const ids = selected.map((c) => c?.id);

  const { data: categories } = useQuery({
    queryKey: ['mainCategories'],
    queryFn: () => getMainCatsClient(true),
  });

  const { data: cat1s, isLoading: loadingCat1 } = useQuery({
    queryKey: ['categories', 1, ids[0]],
    queryFn: () => getSubcatsClient(1, ids[0], true, true),
    enabled: !!ids[0],
  });

  const { data: cat2s, isLoading: loadingCat2 } = useQuery({
    queryKey: ['categories', 2, ids[1]],
    queryFn: () => getSubcatsClient(2, ids[1], true, true),
    enabled: !!ids[1],
  });

  const { data: cat3s, isLoading: loadingCat3 } = useQuery({
    queryKey: ['categories', 3, ids[2]],
    queryFn: () => getSubcatsClient(3, ids[2], true, true),
    enabled: !!ids[2],
  });

  const { data: cat4s, isLoading: loadingCat4 } = useQuery({
    queryKey: ['categories', 4, ids[3]],
    queryFn: () => getSubcatsClient(4, ids[3], true, true),
    enabled: !!ids[3],
  });

  const updateCategory = (index: number, value: string, options: CategoryDocument[]) => {
    const next = [...selected.slice(0, index)];
    next[index] = options.find((o) => o.id === value)!;
    setSelected(next);
    form.clearErrors('category');
  };

  const formatCategoryToOptions = (op: CategoryDocument[] | undefined) => {
    if (op === undefined) {
      return [];
    }

    return op
      ?.filter((o) => !o.is_seo_only)
      .map((section) => {
        return {
          id: section.id,
          label: section.app_desc || section.name,
          value: section.id,
        };
      });
  };


  const renderSubcategorySelect = (
    options: CategoryDocument[] | undefined,
    index: number,
  ) => {

    if (!options?.length) return null;

    return (
      <Select
        options={formatCategoryToOptions(options)}
        value={selected[index]?.id}
        onChange={(option) =>
          updateCategory(index, option.value, options)
        }
        allowEmpty={false}
        className="mb-7 px-8 py-5"
        placeholder="Select a subcategory"
      />
    );
  };

  const selectOptions = formatCategoryToOptions(categories);

  const isLoading = loadingCat1 || loadingCat2 || loadingCat3 || loadingCat4

  const isSelectionComplete = () => {
    const levels = [cat1s, cat2s, cat3s, cat4s];

    for (let i = 0; i < levels.length; i++) {
      const hasOptions = levels[i]?.length;
      const hasSelected = selected[i + 1];

      if (hasOptions && !hasSelected) {
        return false;
      }
    }

    return true;
  };

  const onSave = () => {
    if (!isSelectionComplete()) {
      form.setError('category', {
        type: 'required',
        message: 'Please select all required categories',
      });
      return;
    }

    setCategoryInfo(selected);
    dismiss();
  };

  const onCancel = () => {
    setSelected(categoryInfo ?? []);
    dismiss();
  };

  return (
    <BaseModal
      isOpen={isOpen}
      title={'Edit Sports & Categories'}
      dismiss={dismiss}
      overflowType="visible"
    >
      <div className="flex w-full min-w-[42rem] flex-col gap-[1.6rem] p-8 sm:min-w-[50rem]">
        <FormLabel value="Sport" required>
          <Select
            allowEmpty={false}
            options={selectOptions}
            value={selected[0]?.id}
            onChange={(option) => {
              const main = categories?.find((o) => o.id === option.value)!;
              setSelected([main]);
              form.clearErrors('category');
            }}
          />
        </FormLabel>
        {cat1s?.length ? (
          <FormLabel value="Subcategories" required>
            <div className="flex flex-col gap-y-6">
              {[cat1s, cat2s, cat3s, cat4s].map((cat, i) =>
                renderSubcategorySelect(cat, i + 1)
              )}
            </div>
          </FormLabel>
        ) : null}
        {form.formState.errors.category && (
          <Label className="text-[1.3rem] text-red-500">
            {form.formState.errors.category.message}
          </Label>
        )}
        <div className="mt-[1.6rem] flex gap-[1.6rem]">
          <Button
            width="fluid"
            text="Cancel"
            type="tertiary"
            onClick={onCancel}
          />
          <Button
            width={'fluid'}
            text="Save Changes"
            type="secondary"
            onClick={onSave}
            disabled={!isSelectionComplete() && isLoading}
          />
        </div>
      </div>
    </BaseModal>
  );
}

// function getLabel(c: CategoryDocument) {
//   let label = '';
//   if (c.product_cats.category) {
//     label += `${c.product_cats.category}`;
//   }
//   if (c.product_cats.category1) {
//     label += ` > ${c.product_cats.category1}`;
//   }
//   if (c.product_cats.category2) {
//     label += ` > ${c.product_cats.category2}`;
//   }
//   if (c.product_cats.category3) {
//     label += ` > ${c.product_cats.category3}`;
//   }
//   return label;
// }


{/* <div className="my-8 h-px w-full bg-brand-light-gray" /> */ }
{/* <div className="relative">
          <ComboBox
            options={
              searchedCategories?.map((c) => ({
                id: c.document.id,
                label: getLabel(c.document),
                value: JSON.stringify(c.document),
              })) ?? []
            }
            customIcon={<SearchIcon className="h-12 w-12 text-zinc-700" />}
            placeholder="Search categories"
            value={categorySearch}
            autoComplete="off"
            onInputChange={(e) => setCategorySearch(e.target.value)}
            onChange={(e) => {
              if (e.value) {
                const lastCat = JSON.parse(e.value) as CategoryDocument;
                form.setValue('ride_types', lastCat.ride_types!);
                form.setValue('category', lastCat.product_cats.category!);
                if (lastCat.product_cats.category !== 'Dirt Bikes') {
                  form.setValue('category1', lastCat.product_cats.category1!);
                  form.setValue('category2', lastCat.product_cats.category2!);
                  form.setValue('category3', lastCat.product_cats.category3!);
                }

                form.setValue(
                  'categories',
                  [
                    lastCat.product_cats.category!,
                    lastCat.product_cats.category1!,
                    lastCat.product_cats.category2!,
                    lastCat.product_cats.category3!,
                  ].filter(Boolean)
                );
                dismiss();
              }
            }}
          />
        </div> */}
{/* <FormLabel value="Categories" required={true}>
          <Select
            options={
              categories
                ?.filter((o) => !o.is_seo_only)
                .map((section) => {
                  return {
                    id: section.id,
                    label: section.name,
                    value: section.id,
                  };
                }) ?? []
            }
            value={categoryInfo[1]?.id ?? ''}
            placeholder="Select a category"
            onChange={(option) => {
              const currCategoryInfo = [categoryInfo[0]];
              currCategoryInfo.push(
                categories?.find((o) => o.id === option.value)!
              );
              setCategoryInfo([...currCategoryInfo]);
            }}
            allowEmpty={false}
          />
        </FormLabel> */}
// const [categorySearch, setCategorySearch] = useState('');
// const debouncedSearchTerm = useDebounce(categorySearch, 500);

// const { data: searchedCategories } = useQuery({
//   queryKey: ['categories', debouncedSearchTerm],
//   queryFn: () =>
//     searchCategories(
//       debouncedSearchTerm,
//       categoryInfo[0]?.product_cats.ride_type
//     ),
// });
